import { useEffect, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, MessageCircle, ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GroupsPanel, MonitorPanel, Bubble } from "@/components/MessagingPanels";
import { isAdminRole } from "@/context/ThemeContext";
import { useParentLang, LangSwitch } from "@/lib/parentLang";

const EN = { msgTitle: "Messages", chats: "Chats", groups: "Groups", newChat: "Start a new chat…", noConv: "No conversations yet.", pick: "Pick a conversation", typeMsg: "Type a message", read: "Read", sent: "Sent" };

export default function MessagesPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const { t: pt } = useParentLang();
  const t = isParent ? pt : EN;
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadThreads = () => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
  useEffect(() => { loadThreads(); api.get("/messages/contacts").then((r) => setContacts(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!active) return undefined;
    const load = () => api.get(`/messages/with/${active.id}`).then((r) => setMsgs(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  async function send(e) {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const { data } = await api.post("/messages", { to_user_id: active.id, body: text });
      setMsgs((m) => [...m, data]); setText(""); loadThreads();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  function startWith(id) {
    const c = contacts.find((x) => x.id === id);
    if (c) setActive({ id: c.id, name: c.name, role: c.role });
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Communication" title={t.msgTitle} subtitle={isParent ? t.msgSub : "Chat with parents and colleagues."}>
        {isParent && <LangSwitch />}
      </PageHeader>
      <Tabs defaultValue="chats">
        <TabsList className="bg-violet-100 mb-4">
          <TabsTrigger data-testid="tab-chats" value="chats">{t.chats}</TabsTrigger>
          <TabsTrigger data-testid="tab-groups" value="groups">{t.groups}</TabsTrigger>
          {(isAdminRole(user?.role) || ["principal", "center_head"].includes(user?.role)) && <TabsTrigger data-testid="tab-monitor" value="monitor">Monitor</TabsTrigger>}
        </TabsList>
        <TabsContent value="groups"><GroupsPanel /></TabsContent>
        <TabsContent value="monitor"><MonitorPanel /></TabsContent>
        <TabsContent value="chats">
      <div className="grid md:grid-cols-[320px_1fr] gap-4 min-h-[60vh]">
        <div className={`bg-white rounded-2xl shadow-sm overflow-hidden ${active ? "hidden md:block" : ""}`}>
          <div className="p-3 border-b border-slate-100">
            <Select onValueChange={startWith}>
              <SelectTrigger data-testid="new-chat-select"><SelectValue placeholder={t.newChat} /></SelectTrigger>
              <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} • {ROLE_LABELS[c.role]}</SelectItem>)}</SelectContent>
            </Select>
            {contacts.length === 0 && <p data-testid="no-contacts-hint" className="text-xs text-slate-500 mt-2">{isParent ? t.noContacts : user?.role === "teacher" ? "Set your class on the Home screen to message its parents." : "No one available to message yet."}</p>}
          </div>
          <div className="divide-y divide-slate-100">
            {threads.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">{t.noConv}</p>}
            {threads.map((t) => (
              <button key={t.user_id} data-testid={`thread-${t.user_id}`} onClick={() => setActive({ id: t.user_id, name: t.name, role: t.role })}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-violet-50/60 ${active?.id === t.user_id ? "bg-violet-50" : ""}`}>
                <div className="h-10 w-10 rounded-full brand-header text-white flex items-center justify-center font-semibold shrink-0">{t.name.charAt(0)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between"><span className="font-semibold text-slate-900 truncate">{t.name}</span>{t.unread > 0 && <span className="text-[10px] bg-rose-500 text-white rounded-full px-1.5 py-0.5">{t.unread}</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{t.last.body}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className={`bg-white rounded-2xl shadow-sm flex flex-col ${active ? "" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-10"><MessageCircle size={40} /><p className="mt-2 text-sm">{t.pick}</p></div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                <button data-testid="chat-back" className="md:hidden text-slate-500" onClick={() => setActive(null)}><ArrowLeft size={18} /></button>
                <div><div data-testid="chat-name" className="font-semibold text-slate-900">{active.name}</div><div className="text-xs text-slate-500">{ROLE_LABELS[active.role]}</div></div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[55vh] scrollbar-thin">
                {msgs.map((m) => {
                  const mine = m.from_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "btn-brand rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                        <div className="whitespace-pre-line">{m.body}</div>
                        <div className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}{mine ? (m.read ? ` • ${t.read}` : ` • ${t.sent}`) : ""}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-3 border-t border-slate-100 flex gap-2">
                <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t.typeMsg} />
                <Button data-testid="chat-send" type="submit" className="btn-brand"><Send size={16} /></Button>
              </form>
            </>
          )}
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
